// Admin data layer for Staff (teachers + admins in the org).
// Staff onboarding: admin enters an existing email, we look up the profile
// via rpc_find_profile_by_email, then grant them a role. We never mint auth
// users from the client.

import { supabase, isSupabaseConfigured } from "../supabase";
import { findProfileByEmail } from "./families";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

export { findProfileByEmail };

/**
 * List staff in the org: any profile that has role_assignments(role in
 * teacher|admin|cms_editor) for the admin's org. Pulls current classroom
 * assignments for teachers.
 */
export async function listStaff({ organizationId, search } = {}) {
  ensureLive();
  if (!organizationId) return [];
  const { data: roles, error } = await supabase
    .from("role_assignments")
    .select(`
      id, role, created_at, profile_id,
      profile:profiles(id, full_name, email, phone, avatar_url)
    `)
    .eq("organization_id", organizationId)
    .in("role", ["teacher", "admin", "cms_editor"])
    .order("created_at", { ascending: true });
  if (error) throw error;

  const today = new Date().toISOString().slice(0, 10);
  // Group by profile_id -> aggregate roles[]
  const byProfile = new Map();
  for (const r of roles || []) {
    if (!r.profile) continue;
    const key = r.profile.id;
    if (!byProfile.has(key)) {
      byProfile.set(key, {
        profile: r.profile,
        roles: [],
        role_rows: [],
        assignments: [],
      });
    }
    const entry = byProfile.get(key);
    entry.roles.push(r.role);
    entry.role_rows.push({ id: r.id, role: r.role });
  }

  const profileIds = Array.from(byProfile.keys());
  if (profileIds.length > 0) {
    const { data: assigns, error: aerr } = await supabase
      .from("staff_assignments")
      .select(`
        id, profile_id, classroom_id, position, starts_on, ends_on,
        classroom:classrooms(id, name, age_group)
      `)
      .in("profile_id", profileIds);
    if (aerr) throw aerr;
    for (const a of assigns || []) {
      const entry = byProfile.get(a.profile_id);
      if (!entry) continue;
      entry.assignments.push(a);
    }
  }

  const rows = Array.from(byProfile.values()).map((e) => {
    const active = e.assignments.filter((a) => !a.ends_on || a.ends_on >= today);
    return {
      ...e,
      active_assignments: active,
      active_assignment_count: active.length,
    };
  });
  rows.sort((a, b) => (a.profile.full_name || a.profile.email).localeCompare(b.profile.full_name || b.profile.email));

  if (search) {
    const s = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.profile.full_name || "").toLowerCase().includes(s) ||
        (r.profile.email || "").toLowerCase().includes(s),
    );
  }
  return rows;
}

export async function grantRole({ organizationId, profileId, role }) {
  ensureLive();
  const { data, error } = await supabase
    .from("role_assignments")
    .insert({ organization_id: organizationId, profile_id: profileId, role })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function revokeRole(roleRowId) {
  ensureLive();
  // .select() lets us detect RLS-blocked deletes (0 rows affected returns
  // no error but no data either). The admin_delete policy blocks
  // profile_id = auth.uid() so self-revoke silently fails at the DB — we
  // translate that to a friendly error for the UI.
  const { data, error } = await supabase
    .from("role_assignments")
    .delete()
    .eq("id", roleRowId)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) {
    const err = new Error("You can't revoke your own role.");
    err.code = "self_revoke_blocked";
    throw err;
  }
}

export async function listClassroomsForSelect() {
  ensureLive();
  const { data, error } = await supabase
    .from("classrooms")
    .select("id, name, age_group")
    .order("name", { ascending: true });
  if (error) throw error;
  return data || [];
}

export const ROLE_LABELS = {
  admin:      "Admin",
  teacher:    "Teacher",
  cms_editor: "CMS editor",
  parent:     "Parent",
};

export const ROLE_TONES = {
  admin:      "border-primary/30 bg-primary/10 text-primary-hover",
  teacher:    "border-secondary/50 bg-secondary/30 text-neutral-900",
  cms_editor: "border-neutral-200 bg-neutral-100 text-neutral-600",
  parent:     "border-neutral-200 bg-neutral-100 text-neutral-500",
};
